import type {
  ChaliceStore,
  TypedDispatch,
  TypedGetState,
} from 'src/store/reducer';
import { nudgesUrl } from 'src/config';
import {
  createAuthenticatedHttpClient,
  localiseUrl,
} from 'src/modules/seek-api-request';
import get from 'lodash/get';
import { parse as parseCookies } from 'cookie';
import { metrics } from '@seek/metrics-js';
import type { Country, Nudge, NudgesState } from 'src/types/globals';

const metricsPrefix = 'nudges';

export const CLEAR_NUDGE_ERROR = 'CLEAR_NUDGE_ERROR';
const GET_NUDGES = 'GET_NUDGES';
export const DISMISS_NUDGE = 'DISMISS_NUDGE';
export const POST_NUDGE_ANSWER_SUCCESS = 'POST_NUDGE_ANSWER_SUCCESS';
export const POST_NUDGE_ANSWER_ERROR = 'POST_NUDGE_ANSWER_ERROR';

interface NoPayloadAction {
  type:
    | typeof DISMISS_NUDGE
    | typeof POST_NUDGE_ANSWER_SUCCESS
    | typeof POST_NUDGE_ANSWER_ERROR
    | typeof CLEAR_NUDGE_ERROR;
}

export const initialState = {
  nudges: [],
  currentNudgeIndex: 0,
  nudgePostError: false,
  nudgesCompleted: false,
};

export type Action =
  | {
      type: typeof GET_NUDGES;
      payload: {
        nudgeId?: string;
        position?: number;
        seekerId?: number;
        nudges: Nudge[];
      };
    }
  | NoPayloadAction;

export default function reducer(
  state: NudgesState = initialState,
  action: Action,
): NudgesState {
  switch (action.type) {
    case GET_NUDGES: {
      const nudges = get(action, 'payload.nudges');

      return {
        ...state,
        nudges,
      };
    }

    case POST_NUDGE_ANSWER_SUCCESS: {
      const nextNudgeIndex = state.currentNudgeIndex + 1;
      if (nextNudgeIndex < state.nudges.length) {
        return {
          ...state,
          currentNudgeIndex: nextNudgeIndex,
        };
      }

      // Set nudgesCompleted to true if we reach the end of nudges
      return {
        ...state,
        nudgesCompleted: true,
      };
    }

    case POST_NUDGE_ANSWER_ERROR: {
      return {
        ...state,
        nudgePostError: true,
      };
    }

    case DISMISS_NUDGE: {
      return {
        ...state,
        nudges: [],
      };
    }

    case CLEAR_NUDGE_ERROR: {
      return {
        ...state,
        nudgePostError: false,
      };
    }

    default: {
      return state;
    }
  }
}

export const isNudgeArray = (data: Record<PropertyKey, any>): data is Nudge[] =>
  (Array.isArray(data) && data.length === 0) ||
  (data?.length > 0 && data[0].id !== undefined);

export const getTagValue = (data: Record<PropertyKey, any>): string =>
  isNudgeArray(data) ? (data.length > 0).toString() : typeof data;

export const getNudges =
  ({ country }: { country: Country }) =>
  (dispatch: TypedDispatch, getState: TypedGetState) => {
    const {
      user: { testHeaders },
    } = getState();

    return createAuthenticatedHttpClient({
      defaultRequestConfig: {
        headers: testHeaders,
      },
    })
      .send({
        method: 'post',
        timeout: 5000,
        url: localiseUrl(country, nudgesUrl),
        label: 'nudges',
        data: {
          firstName: getState().user.firstName,
        },
      })
      .then(({ data }: Record<PropertyKey, any>) => {
        metrics.count(metricsPrefix, [`morethanzero:${getTagValue(data)}`]);
        dispatch({
          type: GET_NUDGES,
          payload: {
            nudges: data,
          },
        });
      })
      .catch(() => {
        metrics.count(metricsPrefix, ['fetch:error']);
      });
  };

export const postNudgeResponse =
  ({ nudgeId, ctaValue }: { nudgeId: number; ctaValue: string }) =>
  (dispatch: TypedDispatch, getState: TypedGetState) => {
    const {
      user: { testHeaders },
    } = getState();

    return createAuthenticatedHttpClient({
      defaultRequestConfig: {
        headers: testHeaders,
      },
    })
      .send({
        method: 'put',
        timeout: 2000,
        url: `${localiseUrl(
          getState().location.country,
          nudgesUrl,
        )}/${nudgeId}`,
        label: 'nudges-post',
        headers: {
          'x-seek-ec-sessionid': get(
            parseCookies(document.cookie),
            'JobseekerSessionId',
          ),
        },
        data: { value: ctaValue },
      })
      .then(() => {
        metrics.count(metricsPrefix, ['userresponded:success']);
        dispatch({ type: POST_NUDGE_ANSWER_SUCCESS });
      })
      .catch(() => {
        metrics.count(metricsPrefix, ['userresponded:error']);
        dispatch({ type: POST_NUDGE_ANSWER_ERROR });
      });
  };

export const dismissNudge = (): NoPayloadAction => ({ type: DISMISS_NUDGE });

export const selectNudges = (state: ChaliceStore) => state.nudges.nudges;
export const canShowNudges = (state: ChaliceStore) =>
  state.nudges.currentNudgeIndex === 0 ||
  state.nudges.currentNudgeIndex <= state.nudges.nudges.length;
